import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useSubscriptionPlan, PRO_SOLO_BRANCH_LIMIT, PRO_GROWTH_BRANCH_LIMIT } from "./useSubscriptionPlan";
import type { Database } from "@/integrations/supabase/types";

export type BranchRow = Database["public"]["Tables"]["branches"]["Row"];

export function useBranches() {
  const { businessId } = useAuth();
  const plan = useSubscriptionPlan();
  const [branches, setBranches] = useState<BranchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBranches = useCallback(async () => {
    if (!businessId) {
      setBranches([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const query = supabase
      .from("branches")
      .select("id, business_id, name, created_at")
      .order("created_at", { ascending: true });
    const { data, error: e } = await query.eq("business_id", businessId);
    setLoading(false);
    if (e) {
      setError(e.message);
      setBranches([]);
      return;
    }
    setBranches((data ?? []) as BranchRow[]);
  }, [businessId]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  const branchLimit = plan === "pro_solo" ? PRO_SOLO_BRANCH_LIMIT : plan === "pro_growth" ? PRO_GROWTH_BRANCH_LIMIT : 0;
  const canAddBranch = plan !== "free" && branches.length < branchLimit;
  const atLimit = plan !== "free" && branches.length >= branchLimit;

  return { branches, loading, error, fetchBranches, branchLimit, canAddBranch, atLimit, plan };
}
